import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { debounce } from 'lodash';

export interface UseScrollButtonsParams {
  offset?: number;
  items: any[];
}

const EPSILON = 20;
const DELAY_IN_MILLIS = 100;

export const useScrollButtons = ({ items, offset = 16 }: UseScrollButtonsParams) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldDisplayButtons, setShouldDisplayButtons] = useState(true);
  const [isStartButtonShown, setIsStartButtonShown] = useState(false);
  const [isEndButtonShown, setIsEndButtonShown] = useState(true);

  const handleMove = (direction: 'start' | 'end') => () => {
    const scrollContainer = containerRef.current;
    const dir = direction === 'start' ? -1 : 1;

    if (scrollContainer instanceof HTMLElement) {
      const child = scrollContainer.children[0];
      const scrollBy = (child.clientWidth + offset) * dir;

      scrollContainer.scrollBy({ left: scrollBy, behavior: 'smooth' });
    }
  };

  useLayoutEffect(() => {
    const scrollContainer = containerRef.current;

    if (scrollContainer instanceof HTMLElement) {
      let totalWidth = 0;
      Array.from(scrollContainer.children).forEach(element => {
        totalWidth += element.clientWidth + offset;
      });

      setShouldDisplayButtons(totalWidth > scrollContainer?.clientWidth);
      setIsEndButtonShown(totalWidth > scrollContainer?.clientWidth);
    }
  }, [offset, items]);

  const debouncedOnScroll = useMemo(
    () =>
      debounce((scrollLeft: number, clientWidth: number, scrollWidth: number) => {
        if (scrollLeft === 0) {
          setIsStartButtonShown(false);
        } else {
          setIsStartButtonShown(true);
        }

        if (scrollLeft + clientWidth >= scrollWidth - EPSILON) {
          setIsEndButtonShown(false);
        } else {
          setIsEndButtonShown(true);
        }
      }, DELAY_IN_MILLIS),
    []
  );

  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollLeft, clientWidth, scrollWidth } = event.currentTarget;
    debouncedOnScroll(scrollLeft, clientWidth, scrollWidth);
  };

  const moveToTheStart = handleMove('start');
  const moveToTheEnd = handleMove('end');

  return {
    onScroll,
    containerRef,
    moveToTheEnd,
    moveToTheStart,
    isEndButtonShown,
    isStartButtonShown,
    shouldDisplayButtons,
  };
};
